import { Component, OnInit, OnDestroy, HostListener, Inject } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { HttpEventType, HttpEvent } from "@angular/common/http";
import { Client } from "src/app/client-management/models/client.interface";
import { Patient } from "src/app/patient-management/models/patient.interface";
import { Subscription } from "rxjs";
import { PhotoService, PhotoUploadResponse } from "./photo.service";
import { PatientService } from "src/app/patient-management/services/patient.service";
import { ClientService } from "src/app/client-management/services/client.service";
import ExifReader from 'exifreader';

class PhotoUploadDialogData {
  clientId: string;
  patientId: string;
}
@Component({
  selector: 'app-photo-upload-dialog',
  templateUrl: './photo-upload.dialog.html',
  styleUrls: ['./photo-upload.dialog.scss']
})
export class PhotoUploadDialogComponent implements OnInit, OnDestroy {
  photoTitleControl = new FormControl('', [Validators.required]);
  dateTaken: Date = new Date();  // Default to today
  
  // Data models
  selectedClient: Client = new Client();
  selectedPatient: Patient = new Patient();
  
  // File upload properties
  selectedFile: File | null = null;
  previewUrl: string | ArrayBuffer | null = null;
  previewFailed: boolean = false;
  uploadProgress = 0;
  isUploading = false;
  uploadComplete = false;
  currentFileNameWithoutExt: string = ''; // Store the filename without extension

  allowedFileTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/heic'];
  isMobile: boolean = false;
  
  private subscriptions = new Subscription();

  constructor(
    public dialogRef: MatDialogRef<PhotoUploadDialogComponent>,
    private snackBar: MatSnackBar,
    private photoService: PhotoService,
    private clientService: ClientService,
    private patientService: PatientService,
    @Inject(MAT_DIALOG_DATA) public data: PhotoUploadDialogData
  ) {
    this.checkMobileDevice();
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.checkMobileDevice();
  }

  private checkMobileDevice(): void {
    // // Might want to use something more reliable in the future for future-proofing
    // const userAgentMobile = /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    // this.isMobile = userAgentMobile;

    // If this approach is not sufficient, use the above approach
    this.isMobile = screen.orientation.angle > 0 || screen.orientation.type.startsWith('portrait');
  }

  ngOnInit(): void {
    // Initialize selected client and patient from dialog data
    if (this.data && this.data.clientId) {
      this.subscriptions.add(this.clientService.getClientById(this.data.clientId).subscribe(client => {
        this.selectedClient = client;
      }));
    }
    if (this.data && this.data.patientId) {
      this.subscriptions.add(this.patientService.getPatient(this.data.patientId).subscribe(patient => {
        this.selectedPatient = patient;
      }));
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  setClient(event: Client) {
    this.selectedClient = new Client();
    this.selectedPatient = new Patient();

    setTimeout(() => {
      this.selectedClient = event;
    }, 100);
  }

  setPatient(event: Patient) {
    this.selectedPatient = event;
    if (Patient.getClientReference(event)) {
      this.selectedClient = Patient.getClientReference(event);
    }
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length) {
      const file = input.files[0];

      if (!this.allowedFileTypes.includes(file.type)) {
        this.snackBar.open('Invalid file format. Please select a PNG, JPEG, or HEIC image', 'Close', {
          duration: 5000,
          panelClass: 'error-snackbar'
        });
        return;
      }

      this.selectedFile = file;

      const fileName = file.name.split('.');
      fileName.pop();
      this.currentFileNameWithoutExt = fileName.join('.');

      // Auto-populate the title if it's empty
      if (!this.photoTitleControl.value) {
        this.photoTitleControl.setValue(this.currentFileNameWithoutExt);
      }

      // Extract date taken from EXIF data
      await this.extractDateTaken(file);

      // Always try to generate preview - fallback to placeholder if it fails
      this.previewFailed = false;
      const reader = new FileReader();
      reader.onload = () => {
        this.previewUrl = reader.result;
      };
      reader.readAsDataURL(file);
    }
  }

  private async extractDateTaken(file: File): Promise<void> {
    try {
      const buffer = await this.readFileAsArrayBuffer(file);
      const tags = ExifReader.load(buffer);

      // Try DateTimeOriginal first (when photo was taken), then DateTime
      const dateTag = tags['DateTimeOriginal'] || tags['DateTime'];

      if (dateTag && dateTag.description) {
        // EXIF date format is "YYYY:MM:DD HH:MM:SS"
        const exifDate = dateTag.description;
        const parsedDate = this.parseExifDate(exifDate);

        if (parsedDate) {
          this.dateTaken = parsedDate;
        }
      }
    } catch (error) {
      // EXIF extraction failed - keep the default date (today)
      console.warn('Could not extract EXIF date:', error);
    }
  }

  private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });
  }

  private parseExifDate(exifDateString: string): Date | null {
    // EXIF format: "YYYY:MM:DD HH:MM:SS"
    const match = exifDateString.match(/^(\d{4}):(\d{2}):(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);

    if (match) {
      const [, year, month, day, hour, minute, second] = match;
      return new Date(
        parseInt(year),
        parseInt(month) - 1, // Month is 0-indexed
        parseInt(day),
        parseInt(hour),
        parseInt(minute),
        parseInt(second)
      );
    }

    return null;
  }

  onPreviewError(): void {
    this.previewFailed = true;
  }

  resetFileSelection(): void {
    // Clear title if auto-populated
    if (this.photoTitleControl.value === this.currentFileNameWithoutExt) {
      this.photoTitleControl.setValue('');
    }

    this.selectedFile = null;
    this.previewUrl = null;
    this.previewFailed = false;
    this.currentFileNameWithoutExt = '';
    this.dateTaken = new Date(); // Reset to today
  }

  uploadPhoto(): void {
    if (!this.selectedFile || !this.selectedClient.id || !this.selectedPatient.id) {
      this.snackBar.open('Please select a client, patient, and photo', 'Close', {
        duration: 3000
      });
      return;
    }

    this.isUploading = true;
    this.uploadProgress = 0;

    this.photoService.uploadPatientPhoto(this.selectedPatient.id, this.selectedFile, this.photoTitleControl.value, this.dateTaken)
      .subscribe({
        next: (event: HttpEvent<PhotoUploadResponse>) => {
          if (event.type === HttpEventType.UploadProgress) {
            if (event.total) {
              this.uploadProgress = Math.round((event.loaded / event.total) * 100);
            }
          } else if (event.type === HttpEventType.Response) {
            this.isUploading = false;
            this.uploadComplete = true;
            this.snackBar.open('Photo uploaded successfully', 'Close', {
              duration: 3000
            });
            this.dialogRef.close(true);
          }
        },
        error: (error) => {
          this.isUploading = false;
          this.uploadProgress = 0;
          if (error.status === 0) {
            this.snackBar.open('Network error: Unable to connect to the server', 'Close', {
              duration: 5000,
              panelClass: 'error-snackbar'
            });
            return;
          }
          else {
              this.snackBar.open('Failed to upload photo: ' + error.message, 'Close', {
              duration: 5000,
              panelClass: 'error-snackbar'
            });
          }
        }
      });
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }

  isFormValid(): boolean {
    return (
      this.selectedClient &&
      this.selectedClient.id &&
      this.selectedPatient &&
      this.selectedPatient.id &&
      this.selectedFile &&
      this.photoTitleControl.valid &&
      this.dateTaken != null
    );
  }
}
